import { Link } from '@tanstack/react-router';
import type { AgentApiIamsSidebarItem } from '@/types/serializers';
import useFetchIam from '../api/useFetchIam';
import { useSetAtom } from 'jotai';
import { trialLockedFeatureModalAtom } from '../components/TrialConsultationPopup';
import DashboardIcon from '@/assets/icons/dashboard.svg?react';
import HandshakeIcon from '@/assets/icons/handshake.svg?react';
import PersonIcon from '@/assets/icons/person.svg?react';
import DescriptionIcon from '@/assets/icons/description.svg?react';
import CompanyIcon from '@/assets/icons/company.svg?react';
import ChartIcon from '@/assets/icons/chart.svg?react';
import DownloadIcon from '@/assets/icons/download.svg?react';
import PasteIcon from '@/assets/icons/paste.svg?react';
import ConversionIcon from '@/assets/icons/conversion.svg?react';
import MailIcon from '@/assets/icons/mail.svg?react';
import MediationIcon from '@/assets/icons/mediation.svg?react';
import SettingIcon from '@/assets/icons/setting.svg?react';
import LockIcon from '@/assets/icons/lock.svg?react';
import UsersIcon from '@/assets/icons/person.svg?react';

const iconComponents = {
  home: DashboardIcon,
  dashboard: DashboardIcon,
  handshake: HandshakeIcon,
  users: PersonIcon,
  person: PersonIcon,
  briefcase: DescriptionIcon,
  description: DescriptionIcon,
  building: CompanyIcon,
  company: CompanyIcon,
  chart: ChartIcon,
  download: DownloadIcon,
  paste: PasteIcon,
  conversion: ConversionIcon,
  mail: MailIcon,
  mediation: MediationIcon,
  settings: SettingIcon,
  setting: SettingIcon,
  team: UsersIcon,
} as const;

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  enabled?: boolean;
  onLockedClick?: () => void;
}

function NavItem({ to, icon, label, badge, enabled = true, onLockedClick }: NavItemProps) {
  const IconComponent = iconComponents[icon as keyof typeof iconComponents] || DashboardIcon;
  const linkClassName = 'l-sidebarNavList__link';

  if (!enabled) {
    return (
      <li className="l-sidebarNavListItem">
        <div
          className={linkClassName}
          style={{ opacity: 0.4, cursor: 'pointer' }}
          title="このプランでは利用できません"
          onClick={onLockedClick}
        >
          <IconComponent className="l-sidebarNavList__icon" />
          <span>{label}</span>
          <LockIcon
            className="l-sidebarNavList__icon"
            style={{ marginLeft: 'auto', width: '14px', height: '14px' }}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="l-sidebarNavListItem">
      <Link to={to} className={linkClassName} activeProps={{ className: 'active' }}>
        <IconComponent className="l-sidebarNavList__icon" />
        <span>{label}</span>
        {badge && <span className="l-sidebarNavList__badge">{badge}</span>}
      </Link>
    </li>
  );
}

export default function Sidebar() {
  const { data: iam, isLoading } = useFetchIam();
  const openLockedModal = useSetAtom(trialLockedFeatureModalAtom);
  const currentUser = iam?.current_user;
  const isAdmin = currentUser?.is_admin || currentUser?.is_support;
  const sidebarItems = (iam?.sidebar_items || []) as AgentApiIamsSidebarItem[];

  // サイドバーアイテムを分類
  const utilityKeys = ['export_requests', 'candidate_mails', 'form'];
  const mainItems = sidebarItems.filter(
    (item) =>
      item.key !== 'settings' && !utilityKeys.includes(item.key) && (!item.admin_only || isAdmin)
  );

  const utilityItems = sidebarItems.filter(
    (item) => utilityKeys.includes(item.key) && (!item.admin_only || isAdmin)
  );

  const settingsItem = sidebarItems.find((item) => item.key === 'settings');

  return (
    <aside className="l-sidebar" id="agent-sidebar">
      <div className="l-sidebarHeader">
        <Link to="/agent" className="l-sidebarHeader__link">
          <img
            src="/logo/agenthub_mark_color_type_white.svg"
            className="l-sidebarHeader__logo"
            alt="AgentHub"
          />
        </Link>
      </div>
      <div className="l-sidebarMain">
        <nav className="l-sidebarNav">
          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>読み込み中...</div>
          ) : (
            <>
              <ul className="l-sidebarNavList">
                {mainItems.map((item) => (
                  <NavItem
                    key={item.key}
                    to={item.path}
                    icon={item.icon}
                    label={item.label}
                    badge={item.badge}
                    enabled={item.enabled}
                    onLockedClick={() => openLockedModal(true)}
                  />
                ))}
              </ul>
              {utilityItems.length > 0 && (
                <ul className="l-sidebarNavList">
                  {utilityItems.map((item) => (
                    <NavItem
                      key={item.key}
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      badge={item.badge}
                      enabled={item.enabled}
                      onLockedClick={() => openLockedModal(true)}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </nav>
      </div>
      <div className="l-sidebarBottom">
        <nav className="l-sidebarNav">
          <ul className="l-sidebarNavList Bottom">
            <NavItem to="/agent/linked_accounts" icon="conversion" label="連携先管理" />
            {settingsItem && (
              <NavItem
                to={settingsItem.path}
                icon={settingsItem.icon}
                label={settingsItem.label}
                enabled={settingsItem.enabled}
              />
            )}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
